import { useMemo, useState } from 'react';

import type { InteractiveLessonSectionFragment } from '../lesson-builder.graphql.generated';
import { PagesPreviewModal } from '../components/shared/pages-preview';
import { useImageDimensionEffect } from 'approot/shared/images/images.hooks';
import { usePagesPreview } from '../components/shared/pages-preview.hooks';
import { findImageComponents } from './activity-builder-lightbox.utils';
import {
  useGetInteractiveLessonDataFromCache,
  useGetInteractiveLessonParams,
} from '../activity-builder.hooks';
import { getLargestImage } from '../components/shared/logic/get-largest-image';
import { ComponentCaption } from '../components/shared/component-caption/component-caption';
import * as Styled from './activity-builder-image-lightbox.style';
import { isRichtextEmpty } from 'approot/shared/richtext/richtext.utils';
import {
  ActivityBuilderDualNav,
  ActivityPresentNextControl,
  ActivityPresentPrevControl,
} from '../present/activity-present-nav-controls';
import { ComponentImage } from '../components/component-image';
import ComponentPlayPause from 'approot/shared/play-pause/play-pause';
import { usePausePlaySettings } from 'approot/shared/play-pause/play-pause.hooks';
import {
  SecondaryButton,
  SecondaryButtonIcon,
} from 'approot/shared/styles/buttons/secondary-button.style';
import IconDropdown from 'approot/shared/icons/icon-dropdown';
import type { Pluck } from 'lib/gql/pluck';

const ActivityBuilderImageLightbox = ({
  sectionId,
  selected,
  isStudent,
  onClose,
}: {
  sectionId: string;
  selected: Pluck<InteractiveLessonSectionFragment, 'components'>;
  isStudent?: boolean;
  onClose?: () => void;
}) => {
  const { isPlaying, setIsPlaying } = usePausePlaySettings();

  const { lang, version } = useGetInteractiveLessonParams();
  const { getSection } = useGetInteractiveLessonDataFromCache();
  const [isCaptionCollapsed, setIsCaptionCollapsed] = useState<boolean>(false);

  const imageComponents = useMemo(() => {
    const section = getSection(sectionId, lang, version);
    return findImageComponents(section);
  }, [getSection, sectionId, lang, version]);

  const { index, isLoading, getPrev, getNext, setIsLoading } = usePagesPreview({
    items: imageComponents,
    selected,
    captureKeypressNavigation: true,
  });
  const currentImage = imageComponents[index];
  const isGif = currentImage.__typename === 'ActivitiesGifComponent';

  const altText = useMemo(
    () =>
      isGif
        ? currentImage.properties.image?.alt
        : currentImage.stimulusResourceProperties?.altText ||
          currentImage.properties.altText,
    [isGif, currentImage]
  );

  const { imageDimensions, setContainerElement } = useImageDimensionEffect({
    imageUrl: getLargestImage(
      isGif && !isPlaying
        ? currentImage.properties.printImageUrls
        : currentImage.properties.imageUrls
    ),
    onComplete: () => setIsLoading(false),
  });

  return (
    <PagesPreviewModal
      name="Image lightbox"
      hasCaption={false}
      extraActions={
        isGif && (
          <ComponentPlayPause
            isPlaying={isPlaying}
            altText={altText}
            onClick={() => setIsPlaying(!isPlaying)}
          />
        )
      }
      close={onClose}
      content={
        <>
          <Styled.ImageLightboxImage $isLoading={isLoading}>
            {imageComponents.length > 1 && (
              <>
                {isStudent ? (
                  <>
                    <ActivityPresentPrevControl
                      isStart={index === 0}
                      ariaLabel="image"
                      ariaNavigationLabel="Image pagination"
                      handlePrev={getPrev}
                    />
                    <ActivityPresentNextControl
                      isEnd={index === imageComponents.length - 1}
                      ariaLabel="image"
                      ariaNavigationLabel="Image pagination"
                      handleNext={getNext}
                    />
                  </>
                ) : (
                  <ActivityBuilderDualNav
                    isEnd={index === imageComponents.length - 1}
                    isStart={index === 0}
                    ariaLabel="image"
                    ariaNavigationLabel="Image pagination"
                    handleNext={getNext}
                    handlePrevious={getPrev}
                  />
                )}
              </>
            )}
            <Styled.ImageLightboxWrapper ref={setContainerElement}>
              {!isLoading && (
                <ComponentImage
                  images={
                    isGif && !isPlaying
                      ? currentImage.properties?.printImageUrls || []
                      : currentImage.properties?.imageUrls || []
                  }
                  altText={altText}
                  imageDimensions={imageDimensions}
                />
              )}
            </Styled.ImageLightboxWrapper>

            {!isGif &&
              currentImage.properties.showCaptionOnLesson &&
              !isRichtextEmpty(currentImage.properties.caption) && (
                <Styled.ImageLightboxCaption $isCollapsed={isCaptionCollapsed}>
                  <Styled.ImageLightboxCaptionButtonContainer
                    $isCollapsed={isCaptionCollapsed}
                  >
                    {!isCaptionCollapsed ? (
                      <SecondaryButtonIcon
                        type="button"
                        onClick={() =>
                          setIsCaptionCollapsed(!isCaptionCollapsed)
                        }
                        aria-label="Hide Text"
                        $borderRadius={40}
                      >
                        <IconDropdown width={16} height={16} />
                      </SecondaryButtonIcon>
                    ) : (
                      <SecondaryButton
                        type="button"
                        onClick={() =>
                          setIsCaptionCollapsed(!isCaptionCollapsed)
                        }
                        $borderRadius={40}
                      >
                        Show Text
                      </SecondaryButton>
                    )}
                  </Styled.ImageLightboxCaptionButtonContainer>

                  <ComponentCaption
                    id={currentImage.id}
                    caption={currentImage.properties.caption}
                    showCaption={currentImage.properties.showCaptionOnLesson}
                  />
                </Styled.ImageLightboxCaption>
              )}
          </Styled.ImageLightboxImage>
        </>
      }
    />
  );
};

export default ActivityBuilderImageLightbox;
